/**
 * timing.js — Reusable debounce and rAF-gate utilities.
 */

/** Returns a debounced version of `fn` that waits `ms` after the last call.
 *  .cancel() — discard any pending timeout. */
export function debounce(fn, ms) {
  let id = null;
  const debounced = (...args) => {
    clearTimeout(id);
    id = setTimeout(() => fn(...args), ms);
  };
  debounced.cancel = () => clearTimeout(id);
  return debounced;
}

/** Returns a rAF-gated version of `fn` — at most once per frame, always
 *  using the latest args.
 *  .flush() — execute immediately if a frame is pending.
 *  .cancel() — discard pending frame without executing. */
export function rafGate(fn) {
  let frameId = null;
  let latestArgs = null;
  const gated = (...args) => {
    latestArgs = args;
    if (frameId) return;
    frameId = requestAnimationFrame(() => {
      frameId = null;
      fn(...latestArgs);
      latestArgs = null;
    });
  };
  gated.flush = () => {
    if (frameId) {
      cancelAnimationFrame(frameId);
      frameId = null;
      fn(...latestArgs);
      latestArgs = null;
    }
  };
  gated.cancel = () => {
    if (frameId) {
      cancelAnimationFrame(frameId);
      frameId = null;
      latestArgs = null;
    }
  };
  return gated;
}

/** Standard animation duration (ms) — keep in sync with --pb-transition-slow (0.2s). */
export const ANIM_MS = 200;
